// src/components/sacred/EmptyState.jsx
// Sacred Canvas empty-state primitive. Replaces blank grids on the
// long-tail surfaces (Dashboard, Students, Revision, Certificates,
// Parent Dashboard, Chat). Each call site picks a variant key; the
// variant supplies a glyph slot, a default Arabic copy line, and the
// shape of the primary action. Consumers can override copy/action
// inline — defaults exist so a one-line `<EmptyState variant="..."/>`
// is always meaningful on its own.

import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import {
  Group, Today, SearchOff, EventAvailable, WorkspacePremium, FamilyRestroom,
  ChatBubbleOutline, Inbox,
} from '@mui/icons-material';
import { spaceTone, space } from '../../theme/sacredCanvas';

const VARIANTS = {
  'roster-empty': {
    icon: Group,
    copy: 'لا يوجد طلاب بعد في هذا الفوج',
    actionLabel: 'إضافة طالب',
  },
  'due-today-zero': {
    icon: Today,
    copy: 'لا أوراد مجدولة لهذا اليوم',
    actionLabel: null,
  },
  'search-no-results': {
    icon: SearchOff,
    copy: 'لا توجد نتائج تطابق بحثك',
    actionLabel: null,
  },
  'attendance-not-marked': {
    icon: EventAvailable,
    copy: 'لم يتم تسجيل الحضور لهذا اليوم بعد',
    actionLabel: 'تسجيل الحضور',
  },
  'certificates-none-yet': {
    icon: WorkspacePremium,
    copy: 'لم يُمنح أي إنجاز بعد',
    actionLabel: 'منح إنجاز',
  },
  'parent-no-children-linked': {
    icon: FamilyRestroom,
    copy: 'لم يُربط أي طفل بحسابك بعد',
    actionLabel: 'ربط طفل',
  },
  'chat-no-threads': {
    icon: ChatBubbleOutline,
    copy: 'لا توجد محادثات بعد',
    actionLabel: 'محادثة جديدة',
  },
  'red-flag-empty': {
    icon: EventAvailable,
    copy: 'لا يوجد طلاب بحاجة إلى تنبيه — أحسنت',
    actionLabel: null,
  },
  'generic': {
    icon: Inbox,
    copy: 'لا يوجد محتوى لعرضه بعد',
    actionLabel: null,
  },
};

const HorseshoeGlyph = ({ Icon }) => (
  <Box
    aria-hidden
    className="horseshoe-sm"
    sx={(t) => ({
      width: 96,
      height: 132,
      backgroundColor: t.palette.sacred.surfaceContainerLow,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: t.palette.sacred.primary,
      mb: `${spaceTone.section}px`,
    })}
  >
    <Icon sx={{ fontSize: 44 }} />
  </Box>
);

const EmptyState = ({
  variant = 'generic',
  icon: IconOverride,
  copy: copyOverride,
  actionLabel: actionOverride,
  onAction,
  sx = {},
}) => {
  const v = VARIANTS[variant] || VARIANTS.generic;
  const Icon = IconOverride || v.icon;
  const copy = copyOverride || v.copy;
  const actionLabel = actionOverride !== undefined ? actionOverride : v.actionLabel;

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={(t) => ({
        textAlign: 'center',
        paddingBlock: `${space[10]}px`,
        paddingInline: `${spaceTone.section}px`,
        color: t.palette.sacred.onSurfaceVariant,
        ...sx,
      })}
    >
      <HorseshoeGlyph Icon={Icon} />
      <Typography
        variant="body1"
        sx={(t) => ({
          color: t.palette.sacred.onSurface,
          maxWidth: '32ch',
          mb: actionLabel ? `${spaceTone.section}px` : 0,
        })}
      >
        {copy}
      </Typography>
      {actionLabel && onAction && (
        <Button variant="contained" color="primary" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </Stack>
  );
};

export default EmptyState;
